import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

import API from '../api/axios';
import { Icon } from '../components/SvgIcons';
import './Profile.css';

const Profile = () => {
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile');

  const [twoFA, setTwoFA] = useState({
    qrCode: '',
    secret: '',
    token: '',
    password: '',
    enabled: false,
    loading: false,
    error: '',
    success: ''
  });

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    department: '',
    designation: '',
    experience: '',
    specialization: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });



  const [stats, setStats] = useState({
    totalPapers: 0,
    totalQuestions: 0,
    pendingApprovals: 0,
    completedPapers: 0
  });

  const [accountInfo, setAccountInfo] = useState({
    status: '',
    createdAt: '',
    lastLogin: '',
    college: '',
    department: '',
    university: ''
  });

  useEffect(() => {
    fetchProfileData();
    fetchStats();
    check2FAStatus();
  }, []);



  const fetchProfileData = async () => {
    try {
      const { data } = await API.get('/auth/profile');
      if (data.success) {
        console.log('Profile data received:', data.user); // Debug log
        
        setProfileData({
          name: data.user.name || '',
          email: data.user.email || '',
          phone: data.user.phone || '',
          bio: data.user.bio || '',
          department: data.user.department || '',
          designation: data.user.designation || '',
          experience: data.user.experience || '',
          specialization: data.user.specialization || '',
          address: data.user.address || '',
          city: data.user.city || '',
          state: data.user.state || '',
          country: data.user.country || '',
          pincode: data.user.pincode || ''
        });
        
        setAccountInfo({
          status: data.user.status || 'active',
          createdAt: data.user.created_at || '',
          lastLogin: data.user.last_login || '',
          college: data.user.college_name || 'Not assigned',
          department: data.user.department_name || 'Not assigned',
          university: data.user.university_name || 'Not assigned'
        });

        // 2FA data is handled separately in 2FA Security section




      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await API.get('/auth/profile/stats');
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };



  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const { data } = await API.put('/auth/profile', profileData);
      if (data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update profile' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setSaving(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      setSaving(false);
      return;
    }

    try {
      const { data } = await API.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to change password' 
      });
    } finally {
      setSaving(false);
    }
  };

  const check2FAStatus = async () => {
    try {
      const { data } = await API.get('/auth/2fa/status');
      setTwoFA(prev => ({ ...prev, enabled: data.enabled }));
    } catch { }
  };

  const handle2FASetup = async () => {
    try {
      setTwoFA(prev => ({ ...prev, loading: true, error: '' }));
      const { data } = await API.post('/auth/2fa/setup');
      setTwoFA(prev => ({ ...prev, qrCode: data.qrCode, secret: data.secret }));
    } catch (err) {
      setTwoFA(prev => ({ ...prev, error: err.response?.data?.message || 'Failed to setup 2FA' }));
    } finally {
      setTwoFA(prev => ({ ...prev, loading: false }));
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    try {
      setTwoFA(prev => ({ ...prev, loading: true, error: '' }));
      await API.post('/auth/2fa/verify', { token: twoFA.token });
      setTwoFA(prev => ({ ...prev, success: '2FA enabled successfully!', enabled: true }));
    } catch (err) {
      setTwoFA(prev => ({ ...prev, error: err.response?.data?.message || 'Invalid token' }));
    } finally {
      setTwoFA(prev => ({ ...prev, loading: false }));
    }
  };

  const handle2FADisable = async (e) => {
    e.preventDefault();
    if (!window.confirm('Are you sure you want to disable 2FA?')) return;
    try {
      setTwoFA(prev => ({ ...prev, loading: true, error: '' }));
      await API.post('/auth/2fa/disable', { password: twoFA.password });
      setTwoFA(prev => ({ ...prev, success: '2FA disabled successfully!', enabled: false, password: '', qrCode: '', secret: '' }));
    } catch (err) {
      setTwoFA(prev => ({ ...prev, error: err.response?.data?.message || 'Failed to disable 2FA' }));
    } finally {
      setTwoFA(prev => ({ ...prev, loading: false }));
    }
  };

  const getRoleBadgeClass = (role) => {
    const roleMap = {
      'super_admin': 'badge-danger',
      'moderator': 'badge-warning',
      'examiner': 'badge-success',
      'subject_matter_expert': 'badge-info'
    };
    return roleMap[role] || 'badge-secondary';
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'super_admin': 'Super Admin',
      'moderator': 'Moderator',
      'examiner': 'Examiner',
      'subject_matter_expert': 'Subject Matter Expert'
    };
    return roleMap[role] || role;
  };







  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }



  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-header-content">
          <div className="profile-avatar">
            <div className="avatar-circle">
              {profileData.name.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="profile-header-info">
            <h1 className="profile-name">{profileData.name}</h1>
            <p className="profile-email">{profileData.email}</p>
            <span className={`badge ${getRoleBadgeClass(user.role)}`}>
              {getRoleDisplayName(user.role)}
            </span>
          </div>
        </div>
      </div>

    

      {/* Tabs */}
      <div className="profile-tabs">
        <button 
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <span className="tab-icon"><Icon name="User" size={16} /></span>
          Profile Information
        </button>
        <button 
          className={`tab-button ${activeTab === 'account' ? 'active' : ''}`}
          onClick={() => setActiveTab('account')}
        >
          <span className="tab-icon"><Icon name="Info" size={16} /></span>
          Account Info
        </button>
        <button 
          className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <span className="tab-icon"><Icon name="Lock" size={16} /></span>
          Security
        </button>
        <button 
          className={`tab-button ${activeTab === '2fa' ? 'active' : ''}`}
          onClick={() => setActiveTab('2fa')}
        >
          <span className="tab-icon"><Icon name="Lock" size={16} /></span>
          2FA Security
        </button>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`message ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
          {message.text}
        </div>
      )}

      {/* Tab Content */}
      <div className="profile-content">
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="profile-form">
            <div className="form-section">
              <h3 className="section-title">Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    className="form-input"
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    className="form-input"
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    className="form-input"
                    disabled
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>
            </div>

            {(user.role === 'examiner' || user.role === 'subject_matter_expert') && (
              <div className="form-section">
                <h3 className="section-title">Professional Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <input
                      type="text"
                      name="department"
                      value={profileData.department}
                      onChange={handleProfileChange}
                      className="form-input"
                      placeholder="e.g., Computer Science"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input
                      type="text"
                      name="designation"
                      value={profileData.designation}
                      onChange={handleProfileChange}
                      className="form-input"
                      placeholder="e.g., Assistant Professor"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Experience (Years)</label>
                    <input
                      type="number"
                      name="experience"
                      value={profileData.experience}
                      onChange={handleProfileChange}
                      className="form-input"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Specialization</label>
                    <input
                      type="text"
                      name="specialization"
                      value={profileData.specialization}
                      onChange={handleProfileChange}
                      className="form-input"
                      placeholder="e.g., Machine Learning, AI"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Bio</label>
                  <textarea
                    name="bio"
                    value={profileData.bio}
                    onChange={handleProfileChange}
                    className="form-textarea"
                    rows="4"
                    placeholder="Tell us about yourself..."
                  />
                </div>
              </div>
            )}

            <div className="form-section">
              <h3 className="section-title">Address Information</h3>
              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={profileData.address}
                  onChange={handleProfileChange}
                  className="form-input"
                  placeholder="123 Main Street"
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    value={profileData.city}
                    onChange={handleProfileChange}
                    className="form-input"
                    placeholder="New York"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">State/Province</label>
                  <input
                    type="text"
                    name="state"
                    value={profileData.state}
                    onChange={handleProfileChange}
                    className="form-input"
                    placeholder="NY"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={profileData.country}
                    onChange={handleProfileChange}
                    className="form-input"
                    placeholder="United States"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Postal Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={profileData.pincode}
                    onChange={handleProfileChange}
                    className="form-input"
                    placeholder="10001"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'account' && (
          <div className="account-info-section">
            <div className="form-section">
              <h3 className="section-title">Account Details</h3>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Account Status</div>
                  <div className="info-value">
                    <span className={`badge ${accountInfo.status === 'active' ? 'badge-success' : 'badge-warning'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                      {accountInfo.status === 'active'
                        ? (<><Icon name="Check" size={12} /> Active</>)
                        : (<><Icon name="Clock" size={12} /> {accountInfo.status}</>)}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Role</div>
                  <div className="info-value">
                    <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                      {getRoleDisplayName(user.role)}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">University</div>
                  <div className="info-value">{accountInfo.university || 'Not assigned'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">College</div>
                  <div className="info-value">{accountInfo.college || 'Not assigned'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Department</div>
                  <div className="info-value">{accountInfo.department || 'Not assigned'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Member Since</div>
                  <div className="info-value">
                    {accountInfo.createdAt ? new Date(accountInfo.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'N/A'}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Last Login</div>
                  <div className="info-value">
                    {accountInfo.lastLogin && accountInfo.lastLogin !== 'N/A' ? (
                      new Date(accountInfo.lastLogin).toLocaleString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })
                    ) : 'Never logged in'}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Activity Summary</h3>
              <div className="activity-cards">
                <div className="activity-card">
                  <div className="activity-icon"><Icon name="ChartBar" size={22} /></div>
                  <div className="activity-content">
                    <div className="activity-title">Total Contributions</div>
                    <div className="activity-value">{stats.totalPapers + stats.totalQuestions}</div>
                    <div className="activity-desc">Papers and questions created</div>
                  </div>
                </div>
                <div className="activity-card">
                  <div className="activity-icon"><Icon name="Clock" size={22} /></div>
                  <div className="activity-content">
                    <div className="activity-title">Pending Items</div>
                    <div className="activity-value">{stats.pendingApprovals}</div>
                    <div className="activity-desc">Awaiting your action</div>
                  </div>
                </div>
                <div className="activity-card">
                  <div className="activity-icon"><Icon name="CheckCircle" size={22} color="#16a34a" /></div>
                  <div className="activity-content">
                    <div className="activity-title">Completed</div>
                    <div className="activity-value">{stats.completedPapers}</div>
                    <div className="activity-desc">Successfully finished</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="security-section">
            {/* Password Change Section */}
            <form onSubmit={handlePasswordSubmit} className="profile-form">
              <div className="form-section">
                <h3 className="section-title">
                  <span className="security-icon"><Icon name="Lock" size={18} /></span>
                  Change Password
                </h3>
                <div className="security-info">
                  <p>Ensure your account is using a strong password to stay secure.</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Current Password *</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password *</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    required
                    minLength="6"
                  />
                  <small className="form-hint">Minimum 6 characters</small>
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === '2fa' && (
          <div className="security-section">
            <div className="form-section">
              <h3 className="section-title">
                <span className="security-icon"><Icon name="Lock" size={18} /></span>
                Two-Factor Authentication
              </h3>
              <div className="security-info">
                <p>Add an extra layer of security to your account by requiring a code from your authenticator app.</p>
              </div>

              {twoFA.error && <div className="form-error">{twoFA.error}</div>}
              {twoFA.success && <div className="form-success">{twoFA.success}</div>}

              {twoFA.enabled ? (
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '1.5rem', padding: '1rem', background: 'var(--success-light, #f0fdf4)', borderRadius: '0.5rem', border: '1px solid var(--success, #16a34a)' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>&#10003;</div>
                    <h4 style={{ color: 'var(--success)', margin: '0 0 0.25rem' }}>2FA is Enabled</h4>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>Your account is protected with two-factor authentication</p>
                  </div>
                  <form onSubmit={handle2FADisable}>
                    <div className="form-group">
                      <label className="form-label">Enter your password to disable 2FA</label>
                      <input
                        type="password"
                        className="form-input"
                        value={twoFA.password}
                        onChange={(e) => setTwoFA(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Enter your password"
                        required
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn btn-danger" disabled={twoFA.loading}>
                        {twoFA.loading ? 'Disabling...' : 'Disable 2FA'}
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div>
                  {!twoFA.qrCode ? (
                    <div style={{ textAlign: 'center' }}>
                      <div className="info-box" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
                        <strong>How to set up:</strong>
                        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                          <li>Download Google Authenticator or any TOTP app</li>
                          <li>Click "Setup 2FA" to generate a QR code</li>
                          <li>Scan the QR code with your authenticator app</li>
                          <li>Enter the 6-digit code to verify</li>
                        </ul>
                      </div>
                      <div className="form-actions" style={{ justifyContent: 'center' }}>
                        <button onClick={handle2FASetup} className="btn btn-primary" disabled={twoFA.loading}>
                          {twoFA.loading ? 'Setting up...' : 'Setup 2FA'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h4 style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--text-primary)' }}>Scan QR Code</h4>
                      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                        <div style={{ background: 'white', padding: '0.75rem', borderRadius: '0.75rem', display: 'inline-block', boxShadow: 'var(--shadow-md)' }}>
                          <img src={twoFA.qrCode} alt="QR Code" style={{ width: '200px', height: '200px' }} />
                        </div>
                      </div>
                      <div className="info-box" style={{ marginBottom: '1.5rem' }}>
                        <strong>Manual Entry:</strong>
                        <p style={{ marginTop: '0.5rem', wordBreak: 'break-all', fontFamily: 'monospace', color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                          {twoFA.secret}
                        </p>
                      </div>
                      <form onSubmit={handle2FAVerify}>
                        <div className="form-group">
                          <label className="form-label">Enter 6-digit code from your authenticator app</label>
                          <input
                            type="text"
                            className="form-input"
                            value={twoFA.token}
                            onChange={(e) => setTwoFA(prev => ({ ...prev, token: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                            placeholder="000000"
                            maxLength="6"
                            required
                            style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                          />
                        </div>
                        <div className="form-actions">
                          <button type="submit" className="btn btn-success" disabled={twoFA.loading || twoFA.token.length !== 6}>
                            {twoFA.loading ? 'Verifying...' : 'Verify and Enable'}
                          </button>
                          <button type="button" onClick={() => setTwoFA(prev => ({ ...prev, qrCode: '', secret: '', token: '' }))} className="btn btn-secondary">
                            Back
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Profile;
